import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";
import { inviteResponse, invitesResponse, membersResponse, noContent, type Invite } from "@sitebrew/contracts/v1";

/*
 * Re-exported for the fleet-copied components (`components/user-management`,
 * held byte-for-byte to the archive by `fleet-copy.test.js`), which import
 * their types from here. Nothing is declared here any more.
 */
export type { Invite, Member } from "@sitebrew/contracts/v1";
import { keys } from "./query-keys";

/**
 * Workspace members and invites (`sitebrew-api` design note 0001 §1/§4/§6,
 * M3), the same shape `tokens.ts` already has for keys.
 *
 * Every route carries the workspace in the path, for the same reason
 * `tokens.ts`'s own top note gives: membership is a fact about one workspace,
 * so an owner working in two of them cannot invite into one while reading the
 * other.
 */

/* `Member` and `Invite` are `@sitebrew/contracts/v1`'s (#289 rows 37 and 38); an invite never carries its token. */

/** The live members of one workspace, owner and member alike. */
export function useMembers(workspaceId: string) {
  return useQuery({
    queryKey: keys.members(workspaceId),
    queryFn: () =>
      api(`/workspaces/${workspaceId}/members`, membersResponse).then((body) => body.members),
  });
}

/**
 * Remove a member.
 *
 * Owner-only at the API (`forbidden` otherwise), and refused with
 * `last_owner` for a workspace's only owner — both refusals are the API's own
 * sentences, not reworded here, for the reason `useCreateToken`'s own comment
 * gives.
 */
export function useRemoveMember(workspaceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) =>
      api(`/workspaces/${workspaceId}/members/${userId}`, noContent, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.members(workspaceId) });
    },
  });
}

/**
 * This workspace's pending invites — sent, not yet accepted, not revoked.
 *
 * Owner-only at the API: a `member` calling the same route gets an empty
 * list rather than `forbidden` (the same "scope refusal is a refusal, not
 * an empty list" rule `sites.ts` states does **not** apply here on purpose —
 * `listInvites`'s own doc comment in `sitebrew-api` calls this out
 * explicitly, matching what a member's own console screen would show: nothing
 * to look at). This screen hides the section for a `member` instead of
 * showing an empty table with no way to explain why.
 *
 * `enabled: isOwner` — the API would answer a member's call with an empty
 * list rather than a refusal, so skipping the call is only an efficiency
 * matter here, not a boundary one; the boundary is the API's (DevGuru,
 * review of `sitebrew-console#28`).
 */
export function useInvites(workspaceId: string, isOwner: boolean) {
  return useQuery({
    queryKey: keys.invites(workspaceId),
    queryFn: () =>
      api(`/workspaces/${workspaceId}/invites`, invitesResponse).then((body) => body.invites),
    enabled: isOwner,
  });
}

/**
 * Invite somebody by e-mail.
 *
 * The token is never in this answer — it goes to the invite mail and nowhere
 * else (`sitebrew-api`'s own `publicInvite`). Calling this again for an
 * e-mail that already has a pending invite re-sends it with a fresh token and
 * a fresh expiry; the API does that, not this hook.
 *
 * Unlike `useCreateToken`, this writes the answer into the cache directly
 * rather than invalidating: the created invite is a real fact the API just
 * handed back, and an invalidate-then-refetch would show the dialog closing
 * over a table that still says "no pending invites" for however long the
 * refetch takes — a screen contradicting itself on a page nobody navigated
 * away from.
 */
export function useCreateInvite(workspaceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { email: string; role: "owner" | "member" }) =>
      api(`/workspaces/${workspaceId}/invites`, inviteResponse, {
        method: "POST",
        body: input,
      }).then((body) => body.invite),
    onSuccess: (created) => {
      queryClient.setQueryData<Invite[]>(keys.invites(workspaceId), (old) =>
        old ? [created, ...old] : [created],
      );
    },
  });
}

/** Withdraw a pending invite before it is accepted. */
export function useRevokeInvite(workspaceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (inviteId: string) =>
      api(`/workspaces/${workspaceId}/invites/${inviteId}`, noContent, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.invites(workspaceId) });
    },
  });
}
